import { useGateControllerSearchListQuery } from '@kinetic-ui/data-layer';
import {
  AppSearchIconKey,
  AppSearchItemType,
  DEFAULT_ZOOM,
  MapContext,
  MapSearchResultItem,
  MapSearchResultProps,
  publicUrl,
  useAppSearchHistory,
  useKineticMap,
  useMapBoxSearchContext,
  useMapSearchBoxPin,
  useGetNodes,
} from '@kinetic-ui/shared';
import bbox from '@turf/bbox';
import { BBox2d } from '@turf/helpers/dist/js/lib/geojson';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ENTITY_DRAWER_VIEW_ALL_ROUTE, ENTITY_EDIT_ROUTE } from '../EntityList/const';
import { gateControllerBlack } from '../markers/GateControllerMarker/assets';
import { useTranslation } from 'react-i18next';

const MapSearchResult: React.FC<MapSearchResultProps> = ({ isAppSearch, onResultClick }) => {
  const { selectedLayers, setSelectedLayers, hasEntityDrawer } = useContext(MapContext);
  const { addMapPin, removeMapPin } = useMapSearchBoxPin();
  const { searchText } = useMapBoxSearchContext();
  const { t } = useTranslation('gates');
  const { push } = useHistory();
  const map = useKineticMap();
  const { addSearchHistory } = useAppSearchHistory();

  const { data } = useGateControllerSearchListQuery({
    variables: { searchText, limit: 5 },
    fetchPolicy: 'cache-and-network',
  });

  const gateControllers = useGetNodes(data?.gateControllers);
  const onClick = useCallback(
    (id: string, geometry?: any, openInNewWindow?: boolean) => {
      if (isAppSearch) {
        const path = `/${ENTITY_EDIT_ROUTE}/${id}/`;
        if (openInNewWindow) {
          window.open(publicUrl(path), '_blank');
        } else {
          push(path);
        }
        const gate = gateControllers.find((e) => e.id === id);
        if (gate?.name) {
          addSearchHistory({
            name: gate.name,
            link: path,
            type: AppSearchItemType.View,
            icon: AppSearchIconKey.Gate,
          });
        }
        if (onResultClick) {
          onResultClick();
        }
        return;
      }

      if (!hasEntityDrawer) {
        addMapPin(geometry);
        return;
      }
      !selectedLayers.includes('gates') && setSelectedLayers([...selectedLayers, 'gates']);
      push(`/${ENTITY_DRAWER_VIEW_ALL_ROUTE}/${id}`);
      const bounds = (geometry ? bbox(geometry) : [0, 0, 0, 0]) as BBox2d;
      map.fitBounds([
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ]);
      map.setZoom(DEFAULT_ZOOM);
    },
    [
      addMapPin,
      addSearchHistory,
      gateControllers,
      hasEntityDrawer,
      isAppSearch,
      map,
      onResultClick,
      push,
      selectedLayers,
      setSelectedLayers,
    ]
  );

  useEffect(() => {
    !hasEntityDrawer && searchText?.length > 0 && removeMapPin();
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      {gateControllers.map(({ id, name, location }) => (
        <MapSearchResultItem
          icon={<img src={gateControllerBlack} alt={t('GATES')} />}
          onClick={(openInNewWindow) => onClick(id, location, openInNewWindow)}
          key={`searchItem_${id}`}
          name={name}
          newWindow={isAppSearch}
        />
      ))}
    </>
  );
};

export default MapSearchResult;
